import { MonitorPolicy } from "./MonitorPolicy";
import { Guid } from "../Utils/Guid";
import { MonitorType } from "../Core/MonitorType";
import { HashSet } from "../Utils/HashSet";
import { Device } from "../Core/Device";
import { MonitorEntry } from "../InfoSets/MonitorTable";
import { MonitorSettings_Ping, MonitorSettings_IpEntry } from "./MonitorSettings_Ping";
import { MonitorSettings_SnmpIf, MonitorSettings_SnmpIfEntry } from "./MonitorSettings_SnmpIf";
import { MonitorSettings_Config, MonitorSettings_ConfigEntry } from "./MonitorSettings_Config";
import { MonitorSettings_Snmp, SnmpMonitorEntry } from "./MonitorSettings_Snmp";


export class MonitorPolicyEditor {

	private _originalPolicy: MonitorPolicy;
	private _editPolicy: MonitorPolicy;
	private _hasChanges: boolean;

	public get Policy(): MonitorPolicy {
		return this._editPolicy;
	}
	public get HasPolicy(): boolean {
		return !!this.Policy;
	}

	public get PolicyId(): Guid {
		if (this.HasPolicy) {
			return Guid.FromString(this.Policy.GUID);
		}
		else {
			return Guid.Empty;
		}
	}
	public get HasPolicyId(): boolean {
		if (this.HasPolicy) {
			return this.Policy.HasMonitorPolicyId;
		}
		else {
			return false;
		}
	}

	public get MonitorType(): MonitorType {
		if (this.HasPolicy) {
			return this.Policy.MonitorType;
		}
		else {
			return MonitorType.None;
		}
	}

	// DEVICEs bound to this MonitorPolicy
	public get DeviceGuids(): HashSet<Guid> {
		if (!!this._editPolicy)
			return this._editPolicy.DeviceGuids;

		let deviceGuids = new HashSet<Guid>();
		return deviceGuids;
	}

	public get HasChanges(): boolean {
		return this._hasChanges;
	}
	public set HasChanges(value: boolean) {
		this._hasChanges = value;
	}

	constructor(monitorPolicy: MonitorPolicy) {
		console.assert(!!monitorPolicy);
		// _monitorMap = new Dictionary<Device, MonitorEntry>();

		this._originalPolicy = monitorPolicy;
		//MonitorNetwork = monitorPolicy.Network;

		// CREATE a COPY of the MonitorPolicy
		this._editPolicy = new MonitorPolicy();
		this._editPolicy.Copy(monitorPolicy, false);

		// CREATE COPIES of the MonitorEntries for this MonitorPolicy
		// this.CopyMonitorEntries();

		this.HasChanges = false;
	}

	private Private_AddDevice(device: Device): MonitorEntry {
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return null;

		this._editPolicy.AddDevice(device);

		let monitorEntry: MonitorEntry = null;

		// CHECK to see if we have a MONITOR already
		monitorEntry = this.GetMonitorEntry(device);
		if (!monitorEntry) {
			monitorEntry = this.AddMonitorEntry(device);
		}

		return monitorEntry;
	}

	public AddDevice(device: Device): boolean {
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return false;

		this.HasChanges = true;

		let monitorEntry: MonitorEntry = this.Private_AddDevice(device);
		return !!monitorEntry;
	}

	private AddMonitorEntry(device: Device): MonitorEntry {
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return null;

		let monitorEntry: MonitorEntry = this._editPolicy.AddMonitor_Device(device);
		return monitorEntry;
	}

	public AddPingMonitor(device: Device): boolean {
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return false;

		this.HasChanges = true;

		let monitorAdded = false;

		let monitorEntry: MonitorEntry = this.Private_AddDevice(device);
		if (!!monitorEntry) {
			monitorAdded = monitorEntry.AddPingMonitor(this._editPolicy, device);
		}

		return (monitorAdded);
	}

	public AddSnmpIfMonitor(device: Device, ifIndex: number): boolean {
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return false;
		if (!device.HasIfTable)
			return false;

		this.HasChanges = true;

		let monitorAdded = false;

		let monitorEntry: MonitorEntry = this.Private_AddDevice(device);
		if (!!monitorEntry) {
			if (monitorEntry.AddSnmpIfMonitor(this._editPolicy, device, ifIndex))
				monitorAdded = true;
		}

		return monitorAdded;
	}

	public AddSnmpIfMonitors(device: Device, upOnly: boolean = true): boolean {
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return false;
		if (!device.HasIfTable)
			return false;

		this.HasChanges = true;

		let monitorsAdded = false;

		let monitorEntry: MonitorEntry = this.Private_AddDevice(device);
		if (!!monitorEntry) {
			if (monitorEntry.AddSnmpIfMonitors(this._editPolicy, device, upOnly) > 0)
				monitorsAdded = true;
		}

		return monitorsAdded;
	}

	public AddConfigMonitor(device: Device): boolean {
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return false;

		this.HasChanges = true;

		let monitorAdded = false;

		let monitorEntry: MonitorEntry = this.Private_AddDevice(device);
		if (!!monitorEntry) {
			monitorAdded = monitorEntry.AddConfigMonitor(this._editPolicy, device);
		}

		return (monitorAdded);
	}

	public AddUpdateSnmpMonitorEntry(entry: SnmpMonitorEntry): void {
		this.HasChanges = true;

		// Update policy-level entries
		let policyMonSettings = (this._editPolicy.Settings as MonitorSettings_Snmp);
		policyMonSettings.AddUpdateSnmpMonitorForPolicy(this._editPolicy, entry);

		// Update device-level entries
		this._editPolicy.DeviceGuids.ForEach((deviceGuid: Guid) => {
			let devMonitorEntry = this.GetMonitorEntry_DeviceGuid(deviceGuid);
			devMonitorEntry.AddUpdateSnmpMonitor(this._editPolicy, deviceGuid);
		});
	}

	public RemoveSnmpMonitorEntry(entry: SnmpMonitorEntry): void {
		this.HasChanges = true;

		// Update policy-level entries
		let monSettings = (this._editPolicy.Settings as MonitorSettings_Snmp);
		monSettings.RemoveSnmpOid(entry.Oid);

		// Update device-level entries
		this._editPolicy.DeviceGuids.ForEach((deviceGuid: Guid) => {
			let devMonSettings = this.GetMonitorSettings_Snmp(deviceGuid);
			devMonSettings.RemoveSnmpOid(entry.Oid);
		});
	}

	public AddSnmpMonitorDevice(device: Device): boolean {
		this.HasChanges = true;

		// Add device to policy
		if (this.AddDevice(device)) {

			// Update policy-level entries
			let monSettings = (this._editPolicy.Settings as MonitorSettings_Snmp);
			monSettings.GetGenericEntries().Values().ForEach((entry: SnmpMonitorEntry) => {
				monSettings.AddUpdateSnmpMonitorForDevice(this._editPolicy.MonitorPolicyId, device.GUID, entry);
			});

			// Update device-level entries
			let devMonitorEntry = this.GetMonitorEntry_DeviceGuid(device.GUID);
			devMonitorEntry.AddUpdateSnmpMonitor(this._editPolicy, device.GUID);

			//this._editPolicy.DeviceGuids.ForEach((deviceGuid: Guid) => {
			//	let devMonitorEntry = this.GetMonitorEntry_DeviceGuid(deviceGuid);
			//	devMonitorEntry.AddUpdateSnmpMonitor(this._editPolicy, deviceGuid);
			//});

			return true;
		}
		else {
			return false;
		}
	}

	public RemoveSnmpMonitorDevice(device: Device): void {
		this.HasChanges = true;

		// Remove device from policy's device guids and device monitor map
		this.RemoveDevice(device);

		// Update policy-level entries
		let monSettings = (this._editPolicy.Settings as MonitorSettings_Snmp);
		monSettings.RemoveDevice(device.GUID);

		//// Update device-level entries for remaining devices
		//this._editPolicy.DeviceGuids.ForEach((deviceGuid: Guid) => {
		//	let devMonitorEntry = this.GetMonitorEntry_DeviceGuid(deviceGuid);
		//	devMonitorEntry.AddUpdateSnmpMonitor(this._editPolicy, deviceGuid);
		//});
	}

	public GetMonitorEntry(device: Device): MonitorEntry {
		console.assert(!!device);
		console.assert(device.HasGUID);
		if (!device)
			return null;

		let monitorEntry: MonitorEntry = this._editPolicy.GetMonitor(device);
		return monitorEntry;
	}

	public GetMonitorEntry_DeviceGuid(deviceGuid: Guid): MonitorEntry {
		console.assert(!!deviceGuid);
		if (!deviceGuid)
			return null;

		let monitorEntry: MonitorEntry = this._editPolicy.GetMonitor_DeviceGuid(deviceGuid);
		return monitorEntry;
	}

	public GetMonitorSettings_Ping(device: Device): MonitorSettings_Ping {
		let monitorSettings: MonitorSettings_Ping = null;

		let monitorEntry: MonitorEntry = this.GetMonitorEntry(device);
		if (!!monitorEntry && monitorEntry.HasSettings)
			monitorSettings = monitorEntry.Settings as MonitorSettings_Ping;

		return monitorSettings;
	}

	public GetMonitorSettings_SnmpIf(device: Device): MonitorSettings_SnmpIf {
		let monitorSettings: MonitorSettings_SnmpIf = null;

		let monitorEntry: MonitorEntry = this.GetMonitorEntry(device);
		if (!!monitorEntry && monitorEntry.HasSettings)
			monitorSettings = monitorEntry.Settings as MonitorSettings_SnmpIf;

		return monitorSettings;
	}

	public GetMonitorSettings_Config(device: Device): MonitorSettings_Config {
		let monitorSettings: MonitorSettings_Config = null;

		let monitorEntry: MonitorEntry = this.GetMonitorEntry(device);
		if (!!monitorEntry && monitorEntry.HasSettings)
			monitorSettings = monitorEntry.Settings as MonitorSettings_Config;

		return monitorSettings;
	}

	public GetMonitorSettings_Snmp(deviceGuid: Guid): MonitorSettings_Snmp {
		let monitorSettings: MonitorSettings_Snmp = null;

		let monitorEntry: MonitorEntry = this.GetMonitorEntry_DeviceGuid(deviceGuid);
		if (!!monitorEntry && monitorEntry.HasSettings)
			monitorSettings = monitorEntry.Settings as MonitorSettings_Snmp;

		return monitorSettings;
	}

	public RemoveDevice(device: Device): boolean {
		this.HasChanges = true;
		return this._editPolicy.RemoveDevice(device);
	}

	public Remove_PingIpEntry(device: Device, pingIpEntry: MonitorSettings_IpEntry): boolean {
		if (!device)
			return false;

		this.HasChanges = true;

		let pingSettings: MonitorSettings_Ping = this.GetMonitorSettings_Ping(device);
		if (!!pingSettings) {
			if (!!pingIpEntry) {
				pingSettings.Remove(pingIpEntry);
				if (pingSettings.Count <= 0) {
					this.RemoveDevice(device);
				}
			}
			else {
				this.RemoveDevice(device);
			}
		}
		else {
			this.RemoveDevice(device);
		}

		return true;
	}

	public Remove_SnmpIfEntry(device: Device, snmpIfEntry: MonitorSettings_SnmpIfEntry): boolean {
		if (!device)
			return false;

		this.HasChanges = true;

		let snmpIfSettings: MonitorSettings_SnmpIf = this.GetMonitorSettings_SnmpIf(device);
		if (!!snmpIfSettings) {
			let removeDevice = false;

			if (!!snmpIfEntry) {
				snmpIfSettings.Remove(snmpIfEntry);
				if (snmpIfSettings.Count <= 0)
					removeDevice = true;
			}
			else
				removeDevice = true;

			if (removeDevice)
				this.RemoveDevice(device);
		}
		else {
			this.RemoveDevice(device);
		}

		return true;
	}

	public Remove_ConfigEntry(device: Device, configEntry: MonitorSettings_ConfigEntry): boolean {
		if (!device)
			return false;

		this.HasChanges = true;

		let configSettings: MonitorSettings_Config = this.GetMonitorSettings_Config(device);
		if (!!configSettings) {
			if (!!configEntry) {
				configSettings.Remove(configEntry);
				if (configSettings.Count <= 0) {
					this.RemoveDevice(device);
				}
			}
			else {
				this.RemoveDevice(device);
			}
		}
		else {
			this.RemoveDevice(device);
		}

		return true;
	}

	public CommitChanges(): boolean {
		this._editPolicy.VerifyGuids();

		// Copy the changes back to the _orginalPolicy
		this._originalPolicy.Copy(this._editPolicy, false);

		// This is done on the server
		//let commitOK: boolean = this._originalPolicy.ApplyToDevices();
		//return commitOK;

		this.HasChanges = false;

		return true;
	}

}
